/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'

import { Form, FormFeedback, FormGroup, Label, Row, Col, Input, Button } from 'reactstrap'

import 'react-datetime/css/react-datetime.css';
import Datetime from 'react-datetime';
import InputNumber from 'rc-input-number';
import IPut from 'iput';
import LinuxModeBits from '../widgets/LinuxModeBits'

import {CopyToClipboard} from 'react-copy-to-clipboard';
import { AppContext } from '../AppContext'

class FilenameGenerator extends React.Component
{
  yesterday = Datetime.moment().subtract( 1, 'day' );
  ipAddress = null;
  linuxFileMode = 0;
  linuxSubdirMode = 0;

  ipAddressHelp = 'If checked, the IP address of the computer that uses the generated filename must match the specified IP address.';
  processIdHelp = 'If checked, the process-id of the program that uses the generated filename must match or be a child of the specified process-id.';
  sessionActiveHelp = 'If checked, the user-session in AsterionDB must be active in order for the filename to be valid.';
  tempHandlingHelp = 'If checked, tempoarary-file creation will be enabled in the gateway. This is useful when integrating programs that must open a temporary file in the subirectory of the generated file name.';
  accessModeHelp = "This setting governs how the program that uses the generated filename can open the object. It is not the same as the Linux file mode.";

  constructor(props)
  {
    super();

    this.state =
    {
      generatedFilename: '',
      expirationDate: '',
      customDateDisabled: true,
      numericInputDisabled: true,
      copyDisabled: true,
      generateDisabled: false,
      accessMode: 'R',
      accessMethod: 'PIECE',
      accessLimit: 1,
      ipAddressCheck: false,
      processIdCheck: false,
      sessionCheck: false,
      expireIn: '1 Minute',
      accessLimitRadio: 1,
      allowTempFile: false,
      disableStreamWrite: false,
      subdirUserGroup: '',
      subdirUserGroupValid: undefined,
      subdirUserGroupValidFB: null,
      fileUserGroup: '',
      fileUserGroupValid: undefined,
      fileUserGroupValidFB: null,
      sessionId: null
    }

    this.validDate = this.validDate.bind(this);
    this.customAccessLimitChanged = this.customAccessLimitChanged.bind(this);
    this.setAccessMode = this.setAccessMode.bind(this);
    this.spawnHelperApplication = this.spawnHelperApplication.bind(this);
    this.setExpirationDate = this.setExpirationDate.bind(this);
    this.ipAddressChanged = this.ipAddressChanged.bind(this);
    this.processIdChanged = this.processIdChanged.bind(this);
    this.ipAddressIsValid = this.ipAddressIsValid.bind(this);
    this.setLinuxFileMode = this.setLinuxFileMode.bind(this);
    this.setLinuxSubDirMode = this.setLinuxSubDirMode.bind(this);
  }

  customAccessLimitChanged(valueAsNumber, valueAsString, input)
  {
    this.setState({accessLimit: valueAsNumber});
  }
  
  expirationDateChanged(expireIn, event)
  {
    let disabled = true;
    let customDisabled = true;

    if ('Specified' === expireIn)
    {
      customDisabled = false;
      if ('' !== this.state.expirationDate) disabled = false;
    }
    else
     disabled = false;

    this.setState(
    {
      customDateDisabled: customDisabled, 
      generateDisabled: disabled,
      expireIn: expireIn
    });
  }

  filenameCopied()
  {
    this.context.postNotification('info', 'Gateway filename copied to paste buffer.');
  }

  async generateFilename()
  {
    if (!this.ipAddressIsValid() && this.state.ipAddressCheck)
    {
      this.context.postNotification('error', 'Specify a valid IP Address');
      return;
    }

    let bodyData = {};

    bodyData.gatewayName = this.context.appLocalStorage.getGatewayName();
    bodyData.objectId = this.props.selectedObject.objectId;
    bodyData.accessMode = this.state.accessMode;

    switch (this.state.accessMode)
    {
      case 'R':
        bodyData.linuxFileMode = (this.linuxFileMode + 256).toString(8);
        bodyData.linuxSubdirMode = (this.linuxSubdirMode + 64 + 256).toString(8);
        break;

      case 'W':
        bodyData.linuxFileMode = (this.linuxFileMode + 128).toString(8);
        bodyData.linuxSubdirMode = (this.linuxSubdirMode + 64 + 128 + 256).toString(8);
        break;


      case 'U':
        bodyData.linuxFileMode = (this.linuxFileMode + 128 + 256).toString(8);
        bodyData.linuxSubdirMode = (this.linuxSubdirMode + 64 + 128 + 256).toString(8);
        break;

      default:
        break;
    }

    bodyData.fileUserGroup = this.state.fileUserGroup;
    bodyData.subdirUserGroup = this.state.subdirUserGroup;
    bodyData.validUntil = ('Specified' === this.state.expireIn ? this.state.expirationDate.unix() : this.state.expireIn);
    bodyData.accessLimit = ('custom' === this.state.accessLimitRadio ? this.state.accessLimit : this.state.accessLimitRadio);
    bodyData.accessMethod = this.state.accessMethod;
    bodyData.enforceIpSecurity = (this.state.ipAddressCheck ? 'Y' : 'N');
    bodyData.enforcePidSecurity = (this.state.processIdCheck ? 'Y' : 'N');
    bodyData.sessionMustBeActive = (this.state.sessionCheck ? 'Y' : 'N');
    bodyData.allowTempFile = (this.state.allowTempFile ? 'Y' : 'N');
    bodyData.disableStreamWrite = (this.state.disableStreamWrite ? 'Y' : 'N');
    bodyData.clientProcessId = this.state.processId;
    bodyData.clientIpAddress = this.ipAddress;

    this.setState({generateDisabled: true});

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'generateObjectFilename', bodyData);
    if ('success' !== result.status)
    {
      this.setState({generateDisabled: false, copyDisabled: true});
      return this.context.dbTwig.apiErrorHandler(result, 'Error generating filename');
    } 
    this.setState(
    {
      generatedFilename:  result.jsonData.filename, 
      generateDisabled: false, 
      copyDisabled: false
    });
    this.context.postNotification('info', "Filename generated.  Click on the filename to copy it to the paste buffer.");
  }

  ipAddressChanged(value)
  {
    this.ipAddress = value;
  }

  ipAddressIsValid()
  {
    if (null === this.ipAddress) return false;

    if (this.ipAddress.match(window.ipAddressRegEx))
      return true;
    else
      return false;
  }

  processIdChanged(value)
  {
    this.setState({processId: value});
  }

  renderAccessMode()
  {
    if ('Y' === this.props.selectedObject.versionedObject)
      return (
        <Row title={this.accessModeHelp} style={{paddingTop: "10px"}}>
          <Col sm="2">Access Mode</Col>
          <Col><p>Versioned object - read only access allowed...</p></Col>              
        </Row>
      );
    else if ('shared' === this.props.viewType)
      return (
        <Row title={this.accessModeHelp} style={{paddingTop: "10px"}}>
          <Col sm="2">Access Mode</Col>
          <Col><p>Object shared with domain - read only access allowed...</p></Col>              
        </Row>
        );
    else if ('Y' === this.props.selectedObject.immutableObject)
      return (
        <Row title={this.accessModeHelp} style={{paddingTop: "10px"}}>
          <Col sm="2">Access Mode</Col>
          <Col><p>Immutable object - read only access allowed...</p></Col>              
        </Row>
        );
    else     
      return (
        <div>
          <Row title={this.accessModeHelp} style={{paddingTop: "10px"}}>
            <Col sm="2">Access Mode</Col>
            <Col>
              <Form >
                <FormGroup check className="form-check-inline">
                  <Label check>
                    <Input type="radio" name="accessModeRadios" onChange={() => this.setState({accessMode: 'R'})} 
                      checked={('R' === this.state.accessMode)}
                    /> 
                    Read
                  </Label>
                </FormGroup>
                <FormGroup check className="form-check-inline">
                  <Label check>
                    <Input type="radio" name="accessModeRadios" onChange={() => this.setState({accessMode: 'U'})} 
                      checked={('U' === this.state.accessMode)}
                    /> 
                    Read/Write
                  </Label>
                </FormGroup>
                <FormGroup check className="form-check-inline">
                  <Label check>
                    <Input type="radio" name="accessModeRadios" onChange={() => this.setState({accessMode: 'W'})} 
                      checked={('W' === this.state.accessMode)}
                    /> 
                    Write
                  </Label>
                </FormGroup>
              </Form>
            </Col>              
          </Row>
          <Row style={{paddingTop: "10px"}}>
            <Col sm="2">I/O Options</Col>
            <Col>
              <Form>
                <FormGroup check className="form-check-inline">
                <Label check>
                    <Input type="checkbox" onChange={this.toggleCheckBox.bind(this, 'disableStreamWrite')} 
                      checked={this.state.disableStreamWrite ? "checked" : ''} />
                    Disable Stream Write
                  </Label>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </div>
      );
  }

  render()
  {
    if (undefined === this.props.selectedObject) return false;
  
    let accessMode = this.renderAccessMode();

    return(
      <div>
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">Name</Col>
          <Col>{this.props.selectedObject.objectName}</Col>
        </Row>
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">Object Type</Col>
          <Col>
            {this.props.selectedObject.objectType}
          </Col>
        </Row>
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">Source</Col>
          <Col>
            {this.props.selectedObject.sourcePath}
          </Col>
        </Row>
        {accessMode}
        <Row title="This field allows you to set the traditional Linux file mode bits (e.g. rwxrwxrwx - 777). Note that the user read and write bits are set automatically based upon your selection for the Access Mode field." style={{paddingTop: "10px"}}>
          <Col sm="2">Linux Subdirectory Mode Bits</Col>
          <Col><LinuxModeBits fileOrSubdir={'subdir'} setValue={this.setLinuxSubDirMode}/></Col>
        </Row>
        <Row title="This field allows you to set the traditional Linux file mode bits (e.g. rwxrwxrwx - 777). Note that the user read and write bits are set automatically based upon your selection for the Access Mode field." style={{paddingTop: "10px"}}>
          <Col sm="2">Linux File Mode Bits</Col>
          <Col><LinuxModeBits fileOrSubdir={'file'} setValue={this.setLinuxFileMode}/></Col>
        </Row>
        <Row title="This field allows you to specify the ownership of the generated subdirectory and filename, if necessary. Leaving this field blank causes the filename and subdirectory ownership to be set to the user that is running the DbObscura Gateway that consumes the generated filename (e.g. asterion). Specify this value as 'user:group'."style={{paddingTop: "10px"}}>
          <Col sm="2">Subdir / File Ownership</Col>
          <Col sm='2'>
            <Input type="text" id="subdirUserGroup" 
              value={this.state.subdirUserGroup} onChange={this.setUserGroup.bind(this, 'subdir')} 
              invalid={( undefined ===  this.state.subdirUserGroupValid ? undefined : !this.state.subdirUserGroupValid)} 
              valid={( undefined ===  this.state.subdirUserGroupValid ? undefined : this.state.subdirUserGroupValid)} 
            />
            <FormFeedback valid={false}>{this.state.subdirUserGroupValidFB}</FormFeedback>
          </Col>
          <Col sm='2'>
            <Input type="text" id="subdirUserGroup" 
              value={this.state.fileUserGroup} onChange={this.setUserGroup.bind(this, 'file')} 
              invalid={( undefined ===  this.state.fileUserGroupValid ? undefined : !this.state.fileUserGroupValid)} 
              valid={( undefined ===  this.state.fileUserGroupValid ? undefined : this.state.fileUserGroupValid)} 
            />
            <FormFeedback valid={false}>{this.state.subdirUserGroupValidFB}</FormFeedback>
          </Col>
        </Row>
        <Row style={{paddingTop: "10px"}}>
          <Label sm="2">Access Limit</Label>
          <Col>
            <Form  >          
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="accessLimitRadios" checked={1 === this.state.accessLimitRadio}
                    onChange={this.setAccessLimit.bind(this, 1)}
                  />
                  One Time
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="accessLimitRadios"  checked={-1 === this.state.accessLimitRadio}
                    onChange={this.setAccessLimit.bind(this, -1)}
                  />
                  Unlimited
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="accessLimitRadios"  checked={'custom' === this.state.accessLimitRadio}
                    onChange={this.setAccessLimit.bind(this, 'custom')}
                  />
                  Specified
                </Label>
              </FormGroup>
              <FormGroup className="form-check-inline">
                <InputNumber style={{ width: 100 }} id='accessLimit' min={1} max={99999} disabled={this.state.numericInputDisabled} 
                  onChange={this.customAccessLimitChanged} value={this.state.accessLimit} />
              </FormGroup>
            </Form>
          </Col>
        </Row>
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">Expiration</Col>
          <Col>
            <Form >
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="expirationRadios" checked={('1 Minute' === this.state.expireIn)}
                    onChange={this.expirationDateChanged.bind(this, '1 Minute')} 
                  />
                  1 Minute
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="expirationRadios" checked={('1 Hour' === this.state.expireIn)}
                    onChange={this.expirationDateChanged.bind(this, '1 Hour')} 
                  />
                  1 Hour
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="expirationRadios" checked={('1 Day' === this.state.expireIn)}
                    onChange={this.expirationDateChanged.bind(this, '1 Day')} 
                  />
                  1 Day
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="expirationRadios" checked={('No Expiration' === this.state.expireIn)}
                    onChange={this.expirationDateChanged.bind(this, 'No Expiration')} 
                  />
                  No Expiration
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check>
                  <Input type="radio" name="expirationRadios" checked={('Specified' === this.state.expireIn)}
                    onChange={this.expirationDateChanged.bind(this, 'Specified')}
                  />
                  Specified
                </Label>  
              </FormGroup>
              <FormGroup className="form-check-inline">
                <Datetime onChange={this.setExpirationDate} value={this.state.expirationDate} 
                  inputProps={{disabled: this.state.customDateDisabled, placeholder: "Select a specific date..."}} isValidDate={this.validDate} />
              </FormGroup>
            </Form>
          </Col>
        </Row>
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">Security Settings</Col>
          <Col>
            <Form  >
              <FormGroup check className="form-check-inline">
                <Label check title={this.ipAddressHelp}>
                  <Input type="checkbox" onChange={this.toggleCheckBox.bind(this, 'ipAddressCheck')} 
                    checked={this.state.ipAddressCheck ? "checked" : ''} />
                  IP Address Match
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check title={this.processIdHelp}>
                  <Input type="checkbox" onChange={this.toggleCheckBox.bind(this, 'processIdCheck')} 
                    checked={this.state.processIdCheck ? "checked" : ''} />
                  Process ID Match
                </Label>
              </FormGroup>
              <FormGroup check className="form-check-inline">
                <Label check title={this.sessionActiveHelp}>
                  <Input type="checkbox" onChange={this.toggleCheckBox.bind(this, 'sessionCheck')} 
                    checked={this.state.sessionCheck ? "checked" : ''} />
                  Session Must Be Active
                </Label>
              </FormGroup>
            { 'Y' === this.context.websiteProfile.tempFileHandlingEnabled &&
              <FormGroup check className="form-check-inline" title="Check this box to allow temporary files to be created.">
                <Label check title={this.tempHandlingHelp}>
                  <Input type="checkbox" onChange={this.toggleCheckBox.bind(this, 'allowTempFile')} 
                    checked={this.state.allowTempFile ? "checked" : ''} />
                  Allow Temp File Creation
                </Label>
              </FormGroup>
            }
            </Form>
          </Col>
        </Row>
      { true === this.state.ipAddressCheck && 
        <Row style={{paddingTop: "10px"}}>
          <Col sm='2'><Label>IP Address</Label></Col>
          <Col><IPut onChange={this.ipAddressChanged}/></Col>
        </Row>
      }
      { true === this.state.processIdCheck && 
        <Row style={{paddingTop: "10px"}}>
          <Col sm='2'>Process ID</Col>
          <Col sm='1'><InputNumber value={this.state.processId} onChange={this.processIdChanged} controls={false}/></Col>
        </Row>
      }
        <Row style={{paddingTop: "10px"}}>
          <Col sm="2">
            <Button id="generateButton" disabled={this.state.generateDisabled} onClick={() => this.generateFilename()} color="primary">Generate</Button>
          </Col>
          <Col>
            <CopyToClipboard text={this.state.generatedFilename} onCopy={() => this.filenameCopied()} >
              <span title='Click to copy'><small>{this.state.generatedFilename}</small></span>
            </CopyToClipboard>          
          </Col>
        { 'deleted' !== this.props.viewType && '' !== this.state.generatedFilename &&
          <Col >
            <Button disabled={this.state.copyDisabled} id="spawnHelperApplication" onClick={this.spawnHelperApplication} color="primary">Open w/ Helper App.</Button>
          </Col>
        }          
        </Row>
      </div>
    );
  }

  setAccessLimit(limit)
  {
    let disabled = true;
    let accessLimit = this.state.accessLimit;
    if ('custom' === limit)
    {
      disabled = false;
      accessLimit = 2;
    }

    this.setState(
    {
      accessLimit: accessLimit,
      accessLimitRadio: limit,
      numericInputDisabled: disabled
    });
  }

  setAccessMode(mode)
  {
    this.setState({accessMode: mode});
  }

  setExpirationDate(date)
  {
    let disabled = ('object' === typeof date ? false : true);
    this.setState({expirationDate: date, generateDisabled: disabled});
  }

  setLinuxFileMode(value)
  {
    this.linuxFileMode = value;
  }

  setLinuxSubDirMode(value)
  {
    this.linuxSubdirMode = value;
  }
  
  setUserGroup(element, event)
  {
    if ('file' === element) 
      this.setState({fileUserGroup: event.target.value});
    else
      this.setState({subdirUserGroup: event.target.value});
  }

  async spawnHelperApplication()
  {
    if ('' === this.state.generatedFilename)
    {
      this.context.postNotification('info', 'Generate a filename first.');
      return;
    }

    let helperApp = window.getHelperApp(this.props.selectedObject.mimeType);

    if (undefined === helperApp)
    {
      this.context.postNotification('error', 'A helper application has not been registered for this mime-type.');
      return;
    } 
      
    let bodyData = {};
    bodyData.gatewayName = this.context.appLocalStorage.getGatewayName();
    bodyData.commandLine = helperApp + ' ' + this.state.generatedFilename;

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'spawnHelperApplication', bodyData);
    if ('success' !== result.status && this.context.dbTwig.GatewayOffline !== result.errorCode) 
      return this.context.dbTwig.apiErrorHandler(result, 'Error starting helper app.');

    if ('success' !== result.status && this.context.dbTwig.GatewayOffline === result.errorCode)
      this.context.postNotification('warning', 'Gateway offline.');

    if ('success' === result.status) this.context.postNotification('success', 'Helper application started.');
  }

  toggleCheckBox(element)
  {
    this.setState({ [element]: !this.state[element]});
    if ('ipAddressCheck' === element) this.ipAddress = null;
  }

  validDate(current)
  {
    return current.isAfter(this.yesterday);
  } 
}

FilenameGenerator.contextType = AppContext;

export default FilenameGenerator

/*
      <Row style={{paddingTop: "10px"}}>
        <Col sm="2">Read Method</Col>
        <Col>
          <Form >
            <FormGroup check className="form-check-inline">
              <Label check>
                <Input type="radio" name="accessMethodRadios" onChange={() => this.setState({accessMethod: 'STREAM'})} 
                  checked={('STREAM' === this.state.accessMethod)}
                /> 
                Stream
              </Label>
            </FormGroup>
            <FormGroup check className="form-check-inline">
              <Label check>
                <Input type="radio" name="accessMethodRadios" onChange={() => this.setState({accessMethod: 'PIECE'})} 
                  checked={('PIECE' === this.state.accessMethod)}
                /> 
                Piecemeal
              </Label>
            </FormGroup>
          </Form>
        </Col>              
      </Row>
*/